import { Controller } from "@hotwired/stimulus";

const DIVIDER = "li.ActionList-sectionDivider";
const ITEM = "li.ActionListItem";

/**
 * GR-3758: keep a grouped SelectPanel's headings honest while it filters.
 *
 * Primer draws a group as an ActionList divider carrying the heading followed
 * by that group's items, and its local filter toggles `hidden` on
 * `[role="option"]` items only. A divider is `role="presentation"`, so it is
 * never toggled: filtering to one division left an empty "Partners" heading on
 * screen, and the CSS adjacency rule that hides a divider following a hidden
 * item could take a heading away from a group that still had a match.
 *
 * This sweeps after every filter pass and hides a heading when no item between
 * it and the next heading is visible. The panel filters in the browser with no
 * request to hook, so the trigger is a MutationObserver on the `hidden`
 * attribute the filter itself writes.
 *
 * Items marked `data-ungrouped` (the blank choice and "Other") belong to no
 * group and do not count as content for the heading above them.
 *
 * Hiding is only half of it. Primer also collapses a divider that directly
 * follows a hidden item, in CSS:
 *
 *   .ActionListItem[hidden] + .ActionList-sectionDivider { display: none }
 *
 * which is right for a decorative separator and wrong for a heading, because
 * filtering to one division takes the "Divisions" heading away from its own
 * match. Every heading this decides to keep is marked `data-heading-visible`,
 * and primer-select-panel-overrides.scss restores it.
 */
export default class extends Controller {
  connect() {
    this.sweep();

    this.observer = new MutationObserver((mutations) => {
      // Our own writes land on dividers; sweeping again for them would be a
      // pass that changes nothing.
      if (mutations.every((m) => m.target.matches?.(DIVIDER))) return;

      this.sweep();
    });

    this.observer.observe(this.element, {
      attributes: true,
      attributeFilter: ["hidden"],
      subtree: true,
    });
  }

  disconnect() {
    this.observer?.disconnect();
    this.observer = null;
  }

  sweep() {
    let heading = null;
    let hasVisibleItem = false;

    for (const row of this.element.querySelectorAll(`${DIVIDER}, ${ITEM}`)) {
      if (row.matches(DIVIDER)) {
        this.setHidden(heading, !hasVisibleItem);
        heading = row;
        hasVisibleItem = false;
      } else if (!row.hasAttribute("hidden") && !row.hasAttribute("data-ungrouped")) {
        hasVisibleItem = true;
      }
    }

    this.setHidden(heading, !hasVisibleItem);
  }

  setHidden(element, hidden) {
    if (!element) return;

    if (element.hasAttribute("hidden") !== hidden) {
      if (hidden) {
        element.setAttribute("hidden", "hidden");
      } else {
        element.removeAttribute("hidden");
      }
    }

    if (element.hasAttribute("data-heading-visible") === hidden) {
      if (hidden) {
        element.removeAttribute("data-heading-visible");
      } else {
        element.setAttribute("data-heading-visible", "");
      }
    }
  }
}
